import { useSession } from '@eneco/auth/useSession.js';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import {
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberContractsByContractNumberProducts,
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierProducts,
} from '@eneco/dc-be/client/services.gen.js';
import useAuthenticatedDCBE from '@eneco/dc-be/hooks/useAuthenticatedDCBE.js';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { isAfter, parseISO, subMonths } from 'date-fns';

const ACTIVE_CONTRACT_STATUSES = ['Active', 'RequestedTermination'] as const;

const ACTIVE_CONTRACT_RECENCY_MONTHS = 18;

type UseBillingAccountOptions =
  | { contractNumber: string; addressIdentifier?: never; suspense?: boolean }
  | { addressIdentifier: string; contractNumber?: never; suspense?: boolean };

export const useBillingAccount = ({
  contractNumber,
  addressIdentifier,
  suspense = false,
}: UseBillingAccountOptions) => {
  const { data: session } = useSession();
  const { selectedAccount } = useSelfServiceAccount();

  const { data: contractData, isLoading: isLoadingContract } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberContractsByContractNumberProducts,
    {
      path: { accountNumber: selectedAccount.crmAccountNumber ?? '', contractNumber: contractNumber ?? '' },
    },
    [`/accounts/${selectedAccount.crmAccountNumber}/contracts/${contractNumber}/products`],
    session,
    { shouldFetch: !!contractNumber, suspense },
  );

  const { data: deliveryAddressData, isLoading: isLoadingDelivery } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierProducts,
    {
      path: { accountNumber: selectedAccount.crmAccountNumber ?? '', addressIdentifier: addressIdentifier ?? '' },
    },
    [`/accounts/${selectedAccount.crmAccountNumber}/delivery-addresses/${addressIdentifier}/products`],
    session,
    { shouldFetch: !contractNumber && !!addressIdentifier, suspense },
  );

  const isBillingContextLoading = contractNumber ? isLoadingContract : Boolean(addressIdentifier) && isLoadingDelivery;

  const unwrappedContractData = unwrapData(contractData);
  const unwrappedDeliveryAddressData = unwrapData(deliveryAddressData);

  let billingAccountNumbers: string[];
  if (isBillingContextLoading) {
    billingAccountNumbers = [];
  } else if (unwrappedContractData?.billingAccountNumber) {
    billingAccountNumbers = [unwrappedContractData.billingAccountNumber];
  } else {
    billingAccountNumbers = [
      ...(unwrappedDeliveryAddressData?.activeProducts ?? []),
      ...(unwrappedDeliveryAddressData?.expiredProducts ?? []),
    ]
      .map(product => product.billingAccountNumber)
      .filter((n): n is string => n != null && n !== '');
  }

  const hasBillingAccount = !isBillingContextLoading && billingAccountNumbers.length > 0;

  let hasActiveContract: boolean;
  if (isBillingContextLoading) {
    hasActiveContract = false;
  } else if (unwrappedContractData?.contractProductStatusExternal) {
    hasActiveContract = ACTIVE_CONTRACT_STATUSES.some(s => s === unwrappedContractData.contractProductStatusExternal);
  } else {
    hasActiveContract =
      unwrappedDeliveryAddressData?.activeProducts?.some(product =>
        ACTIVE_CONTRACT_STATUSES.some(s => s === product.contractProductStatusExternal),
      ) ?? false;
  }

  const contractEndDates: string[] = contractNumber
    ? [unwrappedContractData?.activeUntil].filter((date): date is string => !!date)
    : [...(unwrappedDeliveryAddressData?.activeProducts ?? []), ...(unwrappedDeliveryAddressData?.expiredProducts ?? [])]
        .map(product => product.endTime)
        .filter((date): date is string => !!date);

  const recencyThreshold = subMonths(new Date(), ACTIVE_CONTRACT_RECENCY_MONTHS);
  const hasContractEndedWithinRecency = contractEndDates.some(date => isAfter(parseISO(date), recencyThreshold));
  const hasRecentlyActiveContract = !isBillingContextLoading && (hasActiveContract || hasContractEndedWithinRecency);

  return {
    billingAccountNumbers,
    hasBillingAccount,
    hasActiveContract,
    hasRecentlyActiveContract,
    isBillingContextLoading,
  };
};
